// SMSNotifications.jsx
import React, { useState, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSort,
  faSortUp,
  faSortDown,
  faSearch,
  faPaperPlane,
  faInfoCircle,
  faRefresh
} from '@fortawesome/free-solid-svg-icons';

import { SMSAPI } from './SMSAPI';
import './style.css';

const statusSendSMS = {
  error: 'Помилка',
  ok: 'Успішне'
};

const convPhone12ToStr = (p) => {
  if (p.length !== 12) return p;
  const m = p.match(/^(\d{2})(\d{3})(\d{3})(\d{2})(\d{2})/);

  if (!m || m.length !== 6) return p;

  return `+${m[1]} (${m[2]}) ${m[3]}-${m[4]}-${m[5]}`;
};

const prepData = (data) => {
  const result = [];
  let number_row = 0;

  data.forEach((r) => {
    const birth_cert = r.child.birthCertificate.number;

    r.notifications.forEach((n) => {
      number_row += 1;
      const { abonent, target, date, ident } = n;
      const status = n.result;
      const phone = convPhone12ToStr(abonent);

      const row = {
        id: number_row,
        number: number_row,
        birth_cert,
        phone,
        status: statusSendSMS[status] || status,
        date: new Date(date).toLocaleDateString('uk'),
        time: new Date(date).toLocaleTimeString('uk'),
        target,
        ident
      };

      result.push(row);
    });
  });

  return result;
};

const SMSNotifications = () => {
  const threeDaysAgo = new Date();
  threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);

  const [phone, setPhone] = useState('');
  const [showTable, setShowTable] = useState(false);
  const [info, setInfo] = useState([]);
  const [dateFrom, setDateFrom] = useState(threeDaysAgo.toISOString().split('T')[0]);
  const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');

  const columns = [
    { key: 'number', label: '#', sortable: true, width: '5%' },
    { key: 'birth_cert', label: 'Свідоцтво', sortable: true, width: '15%' },
    { key: 'phone', label: 'Номер телефону', sortable: true, width: '15%' },
    { key: 'date', label: 'Дата', sortable: true, width: '10%' },
    { key: 'time', label: 'Час', sortable: true, width: '10%' },
    { key: 'status', label: 'Статус', sortable: true, width: '15%' },
    { key: 'target', label: 'Тип повідомлення', sortable: true, width: '20%' },
    { key: 'ident', label: 'Ідентифікатор', sortable: true, width: '10%' }
  ];

  const setInfoData = useCallback((data) => {
    setInfo(prepData(data));
    setShowTable(true);
    setLoading(false);
  }, []);

  const getInfo = useCallback(async () => {
    setLoading(true);

    try {
      const result = await SMSAPI.getNotifications({ 
        phone, 
        date_from: dateFrom, 
        date_to: dateTo 
      });

      if (result.success) {
        setInfoData(result.data);
      } else {
        alert(`Помилка при отриманні даних: ${result.error}`);
        setLoading(false);
      }
    } catch (error) {
      alert(`Помилка при отриманні даних: ${error.message}`);
      setLoading(false);
    }
  }, [phone, dateFrom, dateTo, setInfoData]);

  const sendTest = useCallback(async () => {
    const messinfo = {
      ok: 'Тестове повідомлення успішно надіслане',
      fall: 'Помилка в роботі сервісу',
      wrongformat: 'Невірний формат номеру телефону'
    };

    if (!phone.match(/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im)) {
      return alert(messinfo.wrongformat);
    }

    try {
      const result = await SMSAPI.sendTestSMS(phone);
      
      if (result.success) {
        alert(messinfo.ok);
      } else {
        alert(`${messinfo.fall}: ${result.error}`);
      }
    } catch (error) {
      alert(`${messinfo.fall}: ${error.message}`);
    }
  }, [phone]);

  const handleSort = useCallback((key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  }, [sortConfig]);

  const handleSearch = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const resetFilters = useCallback(() => {
    const threeDaysAgo = new Date();
    threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);

    setPhone('');
    setDateFrom(threeDaysAgo.toISOString().split('T')[0]);
    setDateTo(new Date().toISOString().split('T')[0]);
    setSearchTerm('');
    setSortConfig({ key: null, direction: 'asc' });
  }, []);

  const getSortIcon = useCallback((columnKey) => {
    if (columnKey !== sortConfig.key) return faSort;
    return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
  }, [sortConfig]);

  const sortedData = useMemo(() => {
    let filteredData = info;
    
    if (searchTerm) {
      filteredData = info.filter(item =>
        Object.values(item).some(value =>
          String(value).toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }
    
    if (!sortConfig.key) return filteredData;
    
    return [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key] || '';
      const bValue = b[sortConfig.key] || '';

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [info, sortConfig, searchTerm]);

  return (
    <main className="queuetable-main-content queuetable-page">
      <div className="container-fluid">

        <div className="queuetable-content-wrapper">
          <div className="queuetable-year-bar d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <span className="queuetable-label text-white-50">
                Управління СМС сповіщеннями
              </span>
            </div>
          </div>

          <div className="card queuetable-table-card mb-4">
            <div className="card-header bg-light">
              <h6 className="mb-0">Фільтри пошуку</h6>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-3">
                  <label className="form-label">Номер телефону</label>
                  <input
                    type="text"
                    className="form-control"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="Введіть номер телефону"
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Дата від</label>
                  <input
                    type="date"
                    className="form-control"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Дата по</label>
                  <input
                    type="date"
                    className="form-control"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </div>
                <div className="col-md-3 d-flex align-items-end">
                  <div className="d-flex gap-2 w-100">
                    <button
                      className="btn btn-primary flex-fill d-flex align-items-center justify-content-center gap-2"
                      onClick={getInfo}
                      disabled={loading}
                    >
                      <FontAwesomeIcon icon={loading ? faRefresh : faSearch} 
                        className={loading ? 'fa-spin' : ''} />
                      {loading ? 'Завантаження...' : 'Отримати інформацію'}
                    </button>
                    <button
                      className="btn btn-outline-secondary"
                      onClick={resetFilters}
                      title="Скинути фільтри"
                    >
                      <FontAwesomeIcon icon={faRefresh} />
                    </button>
                  </div>
                </div>
              </div>

              {showTable && (
                <div className="row mt-3">
                  <div className="col-md-6">
                    <div className="input-group">
                      <span className="input-group-text">
                        <FontAwesomeIcon icon={faSearch} />
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Пошук по всіх полях..."
                        value={searchTerm}
                        onChange={handleSearch}
                      />
                    </div>
                  </div>
                  <div className="col-md-6 d-flex justify-content-end align-items-center">
                    <button
                      className="btn btn-success d-flex align-items-center gap-2"
                      onClick={sendTest}
                    >
                      <FontAwesomeIcon icon={faPaperPlane} />
                      Надіслати тестове повідомлення
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {showTable && (
            <div className="card queuetable-table-card">
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table queuetable-table align-middle mb-0">
                    <thead>
                      <tr>
                        {columns.map((column) => (
                          <th
                            key={column.key}
                            style={{ width: column.width }}
                            onClick={() => column.sortable && handleSort(column.key)}
                            className={column.sortable ? 'cursor-pointer' : ''}
                          >
                            <div className="d-flex align-items-center">
                              {column.label}
                              {column.sortable && (
                                <FontAwesomeIcon
                                  icon={getSortIcon(column.key)}
                                  className="ms-1"
                                  style={{ fontSize: '12px' }}
                                />
                              )}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedData.map((item) => (
                        <tr key={item.id}>
                          {columns.map((column) => (
                            <td key={column.key}>
                              {column.key === 'status' ? (
                                <span
                                  className={`badge ${
                                    item.status === 'Успішне'
                                      ? 'bg-success'
                                      : item.status === 'Помилка'
                                      ? 'bg-danger'
                                      : 'bg-warning'
                                  }`}
                                >
                                  {item[column.key]}
                                </span>
                              ) : (
                                item[column.key]
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {sortedData.length === 0 && (
                  <div className="text-center py-4">
                    <p className="text-muted">Дані не знайдені</p>
                  </div>
                )}

                <div className="d-flex align-items-center justify-content-between mt-4 queuetable-pagination-wrapper">
                  <div className="queuetable-pagination-info">
                    Всього {sortedData.length} записів
                    {searchTerm && (
                      <span className="text-muted ms-2">
                        (знайдено {sortedData.length} з {info.length})
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="alert alert-info mt-3" role="alert">
            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
            <strong>СМС сповіщення</strong> - цей розділ дозволяє переглядати історію СМС сповіщень, 
            фільтрувати їх за датою та номером телефону, а також відправляти тестові повідомлення.
          </div>
        </div>
      </div>
    </main>
  );
};

export default SMSNotifications;